import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
    Users, 
    History, 
    Trash2, 
    Shield, 
    UserPlus, 
    Edit, 
    RefreshCw, 
    Mail, 
    Key, 
    Clock, 
    Eye, 
    EyeOff,
    Search,
    UserCheck,
    UserX,
    Building2,
    Calendar,
    MoreVertical,
    KeyRound,
    MessageSquare,
    ChevronRight,
    Activity,
    AlertCircle
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const ROLES = [
    { value: 'ADMIN', label: 'Administrateur', color: 'bg-red-600', description: 'Accès total, ne pointe pas' },
    { value: 'DIRECTEUR', label: 'Directeur', color: 'bg-amber-500', description: 'Gestion complète, peut pointer' },
    { value: 'DIRECTEUR_ADJOINT', label: 'Directeur Adjoint', color: 'bg-blue-500', description: 'Accès étendu, pas de paie' },
    { value: 'CHEF_CUISINE', label: 'Chef Cuisine', color: 'bg-green-500', description: 'Gestion cuisine' },
    { value: 'CUISINIER', label: 'Cuisinier', color: 'bg-orange-500', description: 'Accès cuisine' },
    { value: 'SERVEUR', label: 'Serveur', color: 'bg-purple-500', description: 'Service en salle' },
    { value: 'EMPLOYE', label: 'Employé', color: 'bg-gray-500', description: 'Accès limité' }
];

const AdminPage = () => {
    const { api, user: currentUser, hasPermission } = useAuth();
    const navigate = useNavigate();
    const isAdmin = currentUser?.role === 'ADMIN';
    const isDirecteur = currentUser?.role === 'DIRECTEUR' || isAdmin;
    
    const [users, setUsers] = useState([]);
    const [loginLogs, setLoginLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    
    // Dialogs
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        username: '',  // Pseudo pour le personnel
        password: '',
        role: 'EMPLOYE',
        pin_code: ''
    });
    
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [usersRes, logsRes] = await Promise.all([
                api().get('/admin/users'),
                api().get('/admin/login-logs')
            ]);
            setUsers(usersRes.data);
            setLoginLogs(logsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            // Préparer les données selon le rôle
            const userData = {
                name: newUser.name,
                password: newUser.password,
                role: newUser.role,
                pin_code: newUser.pin_code || null
            };
            
            // ADMIN utilise email, les autres utilisent pseudo
            if (newUser.role === 'ADMIN') {
                userData.email = newUser.email;
            } else {
                userData.username = newUser.username;
            }
            
            await api().post('/admin/users', userData);
            toast.success('Utilisateur créé avec succès');
            setAddUserDialogOpen(false);
            setNewUser({ name: '', email: '', username: '', password: '', role: 'EMPLOYE', pin_code: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        if (!editingUser) return;
        
        try {
            const updateData = {
                name: editingUser.name,
                role: editingUser.role
            };
            
            await api().put(`/admin/users/${editingUser.id}`, updateData);
            toast.success('Utilisateur modifié');
            setEditUserDialogOpen(false);
            setEditingUser(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleResetPassword = async () => {
        if (!editingUser || !newPassword || newPassword.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        try {
            await api().put(`/admin/users/${editingUser.id}/reset-password`, {
                new_password: newPassword
            });
            toast.success('Mot de passe réinitialisé');
            setResetPasswordDialogOpen(false);
            setNewPassword('');
            setEditingUser(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            await api().put(`/admin/users/${userId}`, { is_active: !currentStatus });
            toast.success(currentStatus ? 'Utilisateur désactivé' : 'Utilisateur activé');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const deleteUser = async (userId) => {
        try {
            await api().delete(`/admin/users/${userId}`);
            toast.success('Utilisateur supprimé');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const getRoleInfo = (role) => {
        return ROLES.find(r => r.value === role) || { value: role, label: role, color: 'bg-gray-500', description: '' };
    };

    const getAvailableRoles = () => {
        if (isAdmin) return ROLES;
        if (currentUser?.role === 'DIRECTEUR') {
            return ROLES.filter(r => !['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(r.value));
        }
        return [];
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            user.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = filterRole === 'all' || !filterRole || user.role === filterRole;
        const matchesStatus = filterStatus === 'all' || !filterStatus || 
                            (filterStatus === 'active' && user.is_active) ||
                            (filterStatus === 'inactive' && !user.is_active);
        return matchesSearch && matchesRole && matchesStatus;
    });

    const stats = {
        total: users.length,
        active: users.filter(u => u.is_active).length,
        inactive: users.filter(u => !u.is_active).length,
        byRole: ROLES.reduce((acc, role) => {
            acc[role.value] = users.filter(u => u.role === role.value).length;
            return acc;
        }, {})
    };

    const getLastLogin = (userId) => {
        const log = loginLogs.find(l => l.user_id === userId);
        if (!log) return null;
        return new Date(log.login_at);
    };

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-foreground tracking-tight flex items-center gap-2">
                        <Shield className="w-7 h-7 text-primary" />
                        Administration
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Gestion des utilisateurs et des accès
                    </p>
                </div>
                
                {isDirecteur && (
                    <Button 
                        onClick={() => setAddUserDialogOpen(true)}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="add-user-btn"
                    >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Nouvel utilisateur
                    </Button>
                )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Users className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.total}</p>
                                <p className="text-xs text-muted-foreground">Utilisateurs</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                <UserCheck className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                                <p className="text-xs text-muted-foreground">Actifs</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                                <UserX className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-red-600">{stats.inactive}</p>
                                <p className="text-xs text-muted-foreground">Inactifs</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{loginLogs.length}</p>
                                <p className="text-xs text-muted-foreground">Connexions</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="users" className="space-y-6">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="users" className="gap-2" data-testid="admin-users-tab">
                        <Users className="w-4 h-4" />
                        Utilisateurs
                    </TabsTrigger>
                    <TabsTrigger value="logs" className="gap-2" data-testid="admin-logs-tab">
                        <History className="w-4 h-4" />
                        Connexions
                    </TabsTrigger>
                </TabsList>

                {/* Users Tab */}
                <TabsContent value="users" className="space-y-4">
                    {/* Filters */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher par nom ou email..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                                <Select value={filterRole} onValueChange={setFilterRole}>
                                    <SelectTrigger className="w-full sm:w-40">
                                        <SelectValue placeholder="Tous les rôles" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les rôles</SelectItem>
                                        {ROLES.map(role => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {role.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={filterStatus} onValueChange={setFilterStatus}>
                                    <SelectTrigger className="w-full sm:w-32">
                                        <SelectValue placeholder="Tous" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="active">Actifs</SelectItem>
                                        <SelectItem value="inactive">Inactifs</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button variant="outline" size="icon" onClick={fetchData}>
                                    <RefreshCw className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Users Grid */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filteredUsers.map((user) => {
                            const roleInfo = getRoleInfo(user.role);
                            const lastLogin = getLastLogin(user.id);
                            const isCurrentUser = user.id === currentUser.id;
                            const isMainAdmin = user.email === 'aschrislay@gmail.com';
                            
                            return (
                                <Card 
                                    key={user.id} 
                                    className={`relative overflow-hidden transition-all hover:shadow-md ${
                                        !user.is_active ? 'opacity-60' : ''
                                    } ${isCurrentUser ? 'ring-2 ring-primary/30' : ''}`}
                                    data-testid={`user-card-${user.id}`}
                                >
                                    {/* Status indicator */}
                                    <div className={`absolute top-0 left-0 right-0 h-1 ${
                                        user.is_active ? 'bg-green-500' : 'bg-red-500'
                                    }`}></div>
                                    
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-12 h-12 rounded-full ${roleInfo.color} text-white flex items-center justify-center font-bold text-lg`}>
                                                    {user.name?.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold">{user.name}</h3>
                                                        {isCurrentUser && (
                                                            <Badge variant="outline" className="text-xs bg-primary/10 text-primary">
                                                                Vous
                                                            </Badge>
                                                        )}
                                                        {isMainAdmin && (
                                                            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                                                Principal
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">
                                                        {user.username ? `@${user.username}` : user.email}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`menu-user-${user.id}`}>
                                                        <MoreVertical className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => {
                                                        setEditingUser({ ...user });
                                                        setEditUserDialogOpen(true);
                                                    }}>
                                                        <Edit className="w-4 h-4 mr-2" />
                                                        Modifier
                                                    </DropdownMenuItem>
                                                    {isAdmin && !isMainAdmin && !isCurrentUser && (
                                                        <DropdownMenuItem onClick={() => {
                                                            setEditingUser(user);
                                                            setResetPasswordDialogOpen(true);
                                                        }}>
                                                            <Key className="w-4 h-4 mr-2" />
                                                            Réinitialiser MDP
                                                        </DropdownMenuItem>
                                                    )}
                                                    {user.staff_id && (
                                                        <DropdownMenuItem onClick={() => navigate(`/staff/${user.staff_id}`)}>
                                                            <Building2 className="w-4 h-4 mr-2" />
                                                            Voir fiche employé
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuSeparator />
                                                    {!isMainAdmin && !isCurrentUser && isDirecteur && (
                                                        <DropdownMenuItem 
                                                            className="text-destructive"
                                                            data-testid={`delete-user-${user.id}`}
                                                            onClick={() => {
                                                                if (window.confirm(`Supprimer ${user.name} ?`)) {
                                                                    deleteUser(user.id);
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" />
                                                            Supprimer
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Badge className={`text-white ${roleInfo.color}`}>
                                                    <Shield className="w-3 h-3 mr-1" />
                                                    {roleInfo.label}
                                                </Badge>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs ${user.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                                        {user.is_active ? 'Actif' : 'Inactif'}
                                                    </span>
                                                    <Switch
                                                        checked={user.is_active}
                                                        onCheckedChange={() => toggleUserStatus(user.id, user.is_active)}
                                                        disabled={isCurrentUser || isMainAdmin}
                                                        data-testid={`toggle-user-${user.id}`}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    Créé {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                                </div>
                                                {lastLogin && (
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        Vu {lastLogin.toLocaleDateString('fr-FR')}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {user.pin_code && (
                                                <div className="flex items-center gap-1 text-xs">
                                                    <KeyRound className="w-3 h-3 text-primary" />
                                                    <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{user.pin_code}</span>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                    
                    {filteredUsers.length === 0 && (
                        <Card>
                            <CardContent className="p-12 text-center">
                                <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Login Logs Tab */}
                <TabsContent value="logs">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <History className="w-5 h-5 text-primary" />
                                Historique des Connexions
                            </CardTitle>
                            <CardDescription>
                                Les 50 dernières connexions
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="h-[500px]">
                                <div className="space-y-2">
                                    {loginLogs.length === 0 ? (
                                        <div className="text-center py-12">
                                            <History className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">Aucune connexion enregistrée</p>
                                        </div>
                                    ) : (
                                        loginLogs.map((log) => {
                                            const roleInfo = getRoleInfo(log.user_role);
                                            const logDate = new Date(log.login_at);
                                            
                                            return (
                                                <div 
                                                    key={log.id} 
                                                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors"
                                                    data-testid={`log-row-${log.id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-full ${roleInfo.color} text-white flex items-center justify-center text-sm font-bold`}>
                                                            {log.user_name?.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-sm">{log.user_name}</p>
                                                            <p className="text-xs text-muted-foreground">{log.user_email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <Badge variant="outline" className={`text-xs text-white ${roleInfo.color}`}>
                                                            {roleInfo.label}
                                                        </Badge>
                                                        <div className="text-right">
                                                            <p className="font-mono text-sm">
                                                                {logDate.toLocaleDateString('fr-FR')}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {logDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Add User Dialog */}
            <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-primary" />
                            Nouvel utilisateur
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddUser} className="space-y-4 mt-2">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom complet *</Label>
                            <Input
                                id="name"
                                value={newUser.name}
                                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                placeholder="Jean Dupont"
                                required
                                data-testid="new-user-name-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="role">Rôle *</Label>
                            <Select 
                                value={newUser.role} 
                                onValueChange={(value) => setNewUser({...newUser, role: value})}
                            >
                                <SelectTrigger data-testid="new-user-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {getAvailableRoles().map(role => (
                                        <SelectItem key={role.value} value={role.value}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${role.color}`}></div>
                                                {role.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {newUser.role && (
                                <p className="text-xs text-muted-foreground">
                                    {getRoleInfo(newUser.role).description}
                                </p>
                            )}
                        </div>
                        
                        {/* Email pour ADMIN, Pseudo pour le personnel */}
                        {newUser.role === 'ADMIN' ? (
                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                    placeholder="admin@aldrins-restaurant.com"
                                    required
                                    data-testid="new-user-email-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    L'administrateur se connecte avec son email
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Label htmlFor="username">Pseudo *</Label>
                                <Input
                                    id="username"
                                    type="text"
                                    value={newUser.username}
                                    onChange={(e) => setNewUser({...newUser, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 20)})}
                                    placeholder="Jean_Dupont"
                                    required
                                    minLength={3}
                                    maxLength={20}
                                    data-testid="new-user-username-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    3-20 caractères (lettres, chiffres, _). Le personnel se connecte avec son pseudo.
                                </p>
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe *</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                                    placeholder="Minimum 6 caractères"
                                    required
                                    minLength={6}
                                    data-testid="new-user-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="pin_code">
                                Code PIN ({newUser.role === 'ADMIN' ? '6' : '4'} chiffres)
                            </Label>
                            <Input
                                id="pin_code"
                                type="text"
                                maxLength={newUser.role === 'ADMIN' ? 6 : 4}
                                pattern={newUser.role === 'ADMIN' ? '[0-9]{6}' : '[0-9]{4}'}
                                value={newUser.pin_code}
                                onChange={(e) => {
                                    const maxLen = newUser.role === 'ADMIN' ? 6 : 4;
                                    setNewUser({...newUser, pin_code: e.target.value.replace(/\D/g, '').slice(0, maxLen)});
                                }}
                                placeholder={newUser.role === 'ADMIN' ? '123456' : '1234'}
                                className="font-mono text-center text-lg tracking-widest"
                                data-testid="new-user-pin-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                Code à {newUser.role === 'ADMIN' ? '6' : '4'} chiffres pour le pointage rapide
                            </p>
                        </div>
                        
                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setAddUserDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" data-testid="create-user-submit">
                                Créer l'utilisateur
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5 text-primary" />
                            Modifier l'utilisateur
                        </DialogTitle>
                    </DialogHeader>
                    {editingUser && (
                        <form onSubmit={handleEditUser} className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label htmlFor="edit-name">Nom complet</Label>
                                <Input
                                    id="edit-name"
                                    value={editingUser.name}
                                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input value={editingUser.email} disabled className="bg-muted" />
                            </div>
                            
                            {isDirecteur && (
                                <div className="space-y-2">
                                    <Label htmlFor="edit-role">Rôle</Label>
                                    <Select 
                                        value={editingUser.role} 
                                        onValueChange={(value) => setEditingUser({...editingUser, role: value})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {getAvailableRoles().map(role => (
                                                <SelectItem key={role.value} value={role.value}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${role.color}`}></div>
                                                        {role.label}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            
                            <DialogFooter className="mt-6">
                                <Button type="button" variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit">
                                    Enregistrer
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="w-5 h-5 text-primary" />
                            Réinitialiser le mot de passe
                        </DialogTitle>
                    </DialogHeader>
                    {editingUser && (
                        <div className="space-y-4 mt-2">
                            <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm">
                                    Utilisateur: <strong>{editingUser.name}</strong>
                                </p>
                                <p className="text-xs text-muted-foreground">{editingUser.email}</p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                                <div className="relative">
                                    <Input
                                        id="new-password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Minimum 6 caractères"
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                                    <p className="text-xs text-amber-700">
                                        L'utilisateur devra utiliser ce nouveau mot de passe lors de sa prochaine connexion.
                                    </p>
                                </div>
                            </div>
                            
                            <DialogFooter>
                                <Button variant="outline" onClick={() => {
                                    setResetPasswordDialogOpen(false);
                                    setNewPassword('');
                                }}>
                                    Annuler
                                </Button>
                                <Button onClick={handleResetPassword} disabled={newPassword.length < 6}>
                                    Réinitialiser
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPage;
